import { useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import { View, StyleSheet, Image, Text } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { resolveClubStartup, resolveCoachStartup } from '@/lib/clubStartup';

const log = createLogger('GATE');

/** Max ms to wait for club startup resolution before falling back to home */
const CLUB_STARTUP_TIMEOUT_MS = 3000;

/**
 * Gate is a zero-UI routing screen. It checks auth + profile state and
 * immediately redirects. No async work — if something is missing the user
 * lands on the appropriate screen within one render cycle.
 *
 * Phase 4A: For returning users with an org_pro subscription, runs
 * resolveClubStartup() before the final navigation. Non-club users are
 * completely unaffected (action: 'none' → existing flow).
 */
export default function GateScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { session, profile, loading, profileLoading } = useAuth();
  const { theme } = useTheme();
  // Reset didNavigate on every mount so remounts (web navigation) work correctly
  const didNavigate = useRef(false);
  // Race condition guard: ensure club routing runs at most once per mount
  const clubRoutingDone = useRef(false);
  // Race condition guard: ensure coach routing runs at most once per mount
  const coachRoutingDone = useRef(false);

  useEffect(() => {
    didNavigate.current = false;
    clubRoutingDone.current = false;
    coachRoutingDone.current = false;
  }, []);

  useEffect(() => {
    if (loading) return;
    if (didNavigate.current) return;

    // Wait for profile fetch to finish — don't route on stale/null profile
    if (session && profileLoading) {
      log.info('[GATE] Profile still loading, waiting...');
      return;
    }

    if (!session) {
      didNavigate.current = true;
      log.info('[GATE] → /(auth)/welcome');
      router.replace('/(auth)/welcome' as any);
      return;
    }

    if (!profile) {
      // Profile hasn't loaded yet — wait, don't redirect to onboarding
      log.info('[GATE] Session exists but profile not loaded yet, waiting...');
      return;
    }

    // Log profile state for debugging routing decisions
    log.info('[GATE] Profile state:', {
      id: profile.id,
      handle: profile.handle,
      onboarding_completed: profile.onboarding_completed,
      tos_accepted_at: profile.tos_accepted_at,
      gym_id: profile.gym_id,
      home_gym_id: profile.home_gym_id,
      display_name: profile.display_name,
      subscription_tier: profile.subscription_tier,
    });

    // Determine if this is a returning/existing user:
    // Any of these signals means they've been through onboarding before
    const isExistingUser = !!(
      profile.onboarding_completed ||
      profile.handle ||
      profile.gym_id ||
      profile.home_gym_id ||
      profile.display_name ||
      profile.lifting_score
    );

    if (isExistingUser && !profile.tos_accepted_at) {
      // Existing user who hasn't accepted ToS yet — sync, no club routing
      didNavigate.current = true;
      log.info('[GATE] → /(onboarding)/terms-accept');
      router.replace('/(onboarding)/terms-accept' as any);
      return;
    }

    if (!isExistingUser) {
      // New user — start onboarding — sync, no club routing
      didNavigate.current = true;
      const gymParam = params.gym ? `?gym=${params.gym}` : '';
      const destination = `/(onboarding)/home-gym${gymParam}`;
      log.info('[GATE] →', destination);
      router.replace(destination as any);
      return;
    }

    // ── Returning user with valid ToS ──────────────────────────────────────
    // Phase 4A: Check for club/org_pro entitlement before landing on home tab.
    // Non-club users: resolveClubStartup returns action:'none' → falls through to /(tabs)/home.
    // Club staff (owner/admin/coach): routed to club-setup or club-home.

    // Guard: only run once per mount (handles StrictMode double-invoke, rapid re-renders)
    if (clubRoutingDone.current) return;
    clubRoutingDone.current = true;
    didNavigate.current = true; // prevent other branches from firing

    const userId = profile.id;
    const subscriptionTier = profile.subscription_tier ?? null;

    // Run async club routing with timeout guard
    const runClubRouting = async () => {
      let result: Awaited<ReturnType<typeof resolveClubStartup>> = {
        action: 'none',
        orgId: null,
        role: null,
      };

      // Timeout: if resolveClubStartup takes > 3s, fall back to home
      const timeoutPromise = new Promise<typeof result>((resolve) => {
        setTimeout(() => {
          log.warn('[GATE] Club startup timed out — falling back to home');
          resolve({ action: 'none', orgId: null, role: null });
        }, CLUB_STARTUP_TIMEOUT_MS);
      });

      try {
        result = await Promise.race([
          resolveClubStartup(userId, subscriptionTier),
          timeoutPromise,
        ]);
      } catch (err: any) {
        log.error('[GATE] resolveClubStartup threw unexpectedly', err?.message);
        // result stays { action: 'none' } — fall through to home
      }

      log.info('[GATE] Club startup result:', result);

      switch (result.action) {
        case 'setup':
          log.info('[GATE] → /club-setup (org not configured)');
          router.replace(`/club-setup?orgId=${result.orgId}` as any);
          return; // do not run coach routing
        case 'home':
          log.info('[GATE] → /club-home (returning club owner/admin/coach)');
          router.replace(`/club-home?orgId=${result.orgId}` as any);
          return; // do not run coach routing
        case 'none':
        default:
          // Fall through to coach routing (club has priority — if club routed, coach is skipped above)
          break;
      }

      // ── Coach startup routing (only if club routing returned 'none') ──────
      // Priority: org_pro (club) > coach_pro. If both tiers are active somehow,
      // club routing wins and coach routing is never reached.
      if (coachRoutingDone.current) {
        log.info('[GATE] → /(tabs)/home (coach routing already done)');
        router.replace('/(tabs)/home' as any);
        return;
      }
      coachRoutingDone.current = true;

      let coachResult: Awaited<ReturnType<typeof resolveCoachStartup>> = {
        action: 'none',
        orgId: null,
      };

      const coachTimeoutPromise = new Promise<typeof coachResult>((resolve) => {
        setTimeout(() => {
          log.warn('[GATE] Coach startup timed out — falling back to home');
          resolve({ action: 'none', orgId: null });
        }, CLUB_STARTUP_TIMEOUT_MS); // reuse same 3s timeout
      });

      try {
        coachResult = await Promise.race([
          resolveCoachStartup(userId, subscriptionTier),
          coachTimeoutPromise,
        ]);
      } catch (err: any) {
        log.error('[GATE] resolveCoachStartup threw unexpectedly', err?.message);
      }

      log.info('[GATE] Coach startup result:', coachResult);

      if (coachResult.action === 'dashboard') {
        log.info('[GATE] → /coach-dashboard (personal coach org)');
        router.replace(`/coach-dashboard?orgId=${coachResult.orgId}` as any);
      } else {
        log.info('[GATE] → /(tabs)/home');
        router.replace('/(tabs)/home' as any);
      }
    };

    runClubRouting();
  }, [session, profile, loading, profileLoading]);

  // Safety: if still on this screen after 8s, force to home or welcome
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!didNavigate.current) {
        // If profile is still loading, don't force-navigate yet
        if (profileLoading) {
          log.warn('[GATE] Timeout reached but profile still loading, extending wait...');
          return;
        }
        didNavigate.current = true;
        const fallback = session ? '/(tabs)/home' : '/(auth)/welcome';
        log.warn('[GATE] Timed out, forcing →', fallback);
        router.replace(fallback as any);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [profileLoading]);

  return (
    <View style={[styles.container, { backgroundColor: '#030303' }]}>
      <Image
        source={require('@/assets/images/duel-logo.png')}
        style={styles.logo}
        resizeMode="contain"
      />
      <Text style={{ color: '#64748B', fontSize: 14, marginTop: 12, fontFamily: 'Inter_500Medium' }}>
        Loading your profile...
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#030303',
  },
  logo: {
    width: 160,
    height: 160,
  },
});
